/*******************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 * Copyright 2025 Adobe
 * All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 ******************************************************************************/

import * as React from "react";
import { useCallback, useState } from "react";
import styled from "styled-components";
import { AppHeader } from "components/shared/AppHeader";
import { AppFooter } from "components/shared/AppFooter";
import {
  ActionContainer,
  BottomCTA,
  FeatureBulletPoint,
  MainCTA,
  Steps,
  UseCaseColumn,
  UseCaseContainer,
  UseCaseContent,
  UseCaseFeature,
  UseCaseTagline,
} from "components/UseCases/Shared";
import { useCreateProject } from "hooks/useCreateProject";

import ChooseMediaButton from "components/UseCases/ChooseMediaButton";
import Analytics, { AnalyticsEvent } from "constants/Analytics";
import { useDispatch } from "react-redux";
import { insertFileAtPlayhead } from "actions/ClipActions";
import { transcribeClip } from "actions/Clips/TranscribeActions";
import { v4 as uuidv4 } from "uuid";

import dropVideoImg from "./images/drop-video-illustration.svg";
import customizeCaptionsImg from "./images/customize-captions@2x.png?responsive";
import easyToFollowCaptionsImg from "./images/easy-to-follow-captions@2x.png?responsive";
import enhanceSpeechImg from "components/UseCases/images/enhance-speech@2x.jpg?responsive";

import iconGlobe from "components/UseCases/images/icon-globe.svg";
import iconInSync from "components/UseCases/images/icon-in-sync.svg";
import iconAspect from "components/UseCases/images/icon-aspect.svg";
import iconUser from "components/UseCases/images/icon-user.svg";
import iconPencil from "components/UseCases/images/icon-pencil.svg";
import iconColorPalette from "components/UseCases/images/icon-color-palette.svg";
import iconAudioWave from "components/UseCases/images/icon-audio-wave.svg";
import iconSpeaker from "components/UseCases/images/icon-speaker.svg";
import iconFaders from "components/UseCases/images/icon-faders.svg";

import { VideoGallery, VideoMetadata } from "components/UseCases/VideoGallery";
import { ScrollToTop } from "components/shared/ScrollToTop";
import {
  DocumentDescription,
  DocumentImagePreview,
  DocumentTitle,
} from "components/shared/SEOHelpers";
import { devices } from "components/shared/Styles";
import {
  ExportPages,
  useExportNavigation,
} from "components/Studio/Export/useExportNavigation";
import { ACCEPTED_VIDEO_CONTENT_TYPES } from "constants/AcceptedFiles";
import { addToast } from "actions/ToastMessageActions";

const easyToFollowCaptionsBulletPoints: FeatureBulletPoint[] = [
  {
    text: I18n.t("use_cases.features.easy_to_follow_captions.languages"),
    icon: iconGlobe,
  },
  {
    text: I18n.t("use_cases.features.easy_to_follow_captions.in_sync"),
    icon: iconInSync,
  },
  {
    text: I18n.t("use_cases.features.easy_to_follow_captions.editing"),
    icon: iconPencil,
  },
];

const customizeCaptionsBulletPoints: FeatureBulletPoint[] = [
  {
    text: I18n.t("use_cases.features.customize_captions.theme"),
    icon: iconColorPalette,
  },
  {
    text: I18n.t("use_cases.features.customize_captions.speaker_names"),
    icon: iconUser,
  },
  {
    text: I18n.t("use_cases.features.customize_captions.aspect_ratios"),
    icon: iconAspect,
  },
];

const enhanceSpeechBulletPoints: FeatureBulletPoint[] = [
  {
    text: I18n.t("use_cases.features.enhance_speech.background_noise"),
    icon: iconAudioWave,
  },
  {
    text: I18n.t("use_cases.features.enhance_speech.automatic_leveling"),
    icon: iconSpeaker,
  },
  {
    text: I18n.t("use_cases.features.enhance_speech.adjust_strength"),
    icon: iconFaders,
  },
];

const VIDEOS: VideoMetadata[] = [
  // TODO: Awaiting video assets from design...
  // {
  //   id: "analysis-video",
  //   src: analysisVideo,
  //   posterSrc: analysisPosterImg,
  //   gridColumn: "1",
  //   gridRow: "1",
  //   hideOnMobile: false,
  // },
  // {
  //   id: "crime-time-video",
  //   src: crimeTimeVideo,
  //   posterSrc: crimeTimePosterImg,
  //   gridColumn: "2",
  //   gridRow: "1",
  //   hideOnMobile: false,
  // },
  // {
  //   id: "trend-break-video",
  //   src: trendBreakVideo,
  //   posterSrc: trendBreakPosterImg,
  //   gridColumn: "3",
  //   gridRow: "1",
  //   hideOnMobile: false,
  // },
];

const VideoGallerySection = styled.div`
  margin-top: 48px;

  ${devices.large} {
    margin-top: 80px;
  }

  > * {
    margin-bottom: 48px;
  }

  > h3 {
    color: #000;
    text-align: center;
    font-size: 36px;
    font-style: normal;
    font-weight: 900;
    line-height: 130%; /* 46.8px */
  }
`;

const CaptionsPage: React.FC = () => {
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const { goToExportPage } = useExportNavigation();
  const dispatch = useDispatch();
  const { createProject } = useCreateProject();

  const onFileSelected = useCallback(
    async (file: File) => {
      if (!ACCEPTED_VIDEO_CONTENT_TYPES.includes(file.type)) {
        dispatch(addToast({ message: I18n.t("load_video_file_error") }));
        return;
      }

      Analytics.instance.logEvent(AnalyticsEvent.USE_CASE_PAGE_FILE_SELECTED);

      setIsLoading(true);
      const newProject = await createProject();

      if (!newProject) {
        setIsLoading(false);
        return;
      }

      const newClipId = uuidv4();
      await dispatch(
        insertFileAtPlayhead({
          file: file,
          clipId: newClipId,
        })
      );
      dispatch(transcribeClip({ clipId: newClipId }));
      goToExportPage({
        page: ExportPages.VIDEOS,
        options: { usecase: true, load_with_captions: true },
        projectUuid: newProject.id,
      });
      Analytics.instance.logEvent(AnalyticsEvent.USE_CASE_ADDED_CAPTIONS);
    },
    [createProject, dispatch, goToExportPage]
  );

  const actionContainer = (
    <ActionContainer
      title={I18n.t("use_cases.add_captions_to_video_page.get_started")}
      image={dropVideoImg}
      actionButton={
        <ChooseMediaButton
          disabled={isLoading}
          handleFile={onFileSelected}
          videoOnly={true}
        />
      }
    />
  );

  return (
    <>
      <UseCaseContent>
        <DocumentTitle
          title={I18n.t("use_cases.add_captions_to_video_page.title")}
        />
        <DocumentDescription
          description={I18n.t(
            "use_cases.add_captions_to_video_page.seo_description"
          )}
        />
        <DocumentImagePreview
          url={`${process.env.CDN_ROOT}/add-captions-to-video.png`}
        />
        <AppHeader />
        <ScrollToTop />

        <UseCaseColumn>
          <MainCTA
            title={I18n.t("use_cases.add_captions_to_video_page.title")}
            body={I18n.t("use_cases.add_captions_to_video_page.description")}
            actionContainer={actionContainer}
            handleDroppedFile={onFileSelected}
            acceptedContentTypes={ACCEPTED_VIDEO_CONTENT_TYPES}
          />

          <UseCaseContainer>
            <UseCaseTagline>
              {I18n.t("use_cases.add_captions_to_video_page.tagline")}
            </UseCaseTagline>
            <Steps stepPrefix={"add_captions_to_video_page"} />
            {/* TODO: Awaiting video assets from design... */}
            {/*<VideoGallerySection>*/}
            {/*  <h3>*/}
            {/*    {I18n.t(*/}
            {/*      "use_cases.add_captions_to_video_page.video_gallery.title"*/}
            {/*    )}*/}
            {/*  </h3>*/}
            {/*  <VideoGallery*/}
            {/*    videos={VIDEOS}*/}
            {/*  />*/}
            {/*</VideoGallerySection>*/}
            <UseCaseFeature
              srcSet={easyToFollowCaptionsImg.srcSet}
              title={I18n.t("use_cases.features.easy_to_follow_captions.title")}
              body={I18n.t("use_cases.features.easy_to_follow_captions.body")}
              bulletPoints={easyToFollowCaptionsBulletPoints}
            />
            <UseCaseFeature
              srcSet={customizeCaptionsImg.srcSet}
              title={I18n.t("use_cases.features.customize_captions.title")}
              body={I18n.t("use_cases.features.customize_captions.body")}
              bulletPoints={customizeCaptionsBulletPoints}
            />
            <UseCaseFeature
              srcSet={enhanceSpeechImg.srcSet}
              title={I18n.t("use_cases.features.enhance_speech.title")}
              body={I18n.t("use_cases.features.enhance_speech.body")}
              bulletPoints={enhanceSpeechBulletPoints}
            />
          </UseCaseContainer>

          <BottomCTA
            title={I18n.t(
              "use_cases.add_captions_to_video_page.bottom_action.title"
            )}
            subtitle={I18n.t(
              "use_cases.add_captions_to_video_page.bottom_action.text"
            )}
            actionButton={
              <ChooseMediaButton
                disabled={isLoading}
                handleFile={onFileSelected}
                videoOnly={true}
              />
            }
          />
        </UseCaseColumn>
      </UseCaseContent>
      <AppFooter />
    </>
  );
};

export { CaptionsPage };
